import { Middleware } from "@reduxjs/toolkit";
import { errorType, ErrorType } from "../../types/error.definition";
import { setError } from "../reducers/errorSlice";
import { hideLoader } from "../reducers/loaderSlice";

/**
 * Middleware to handle errors from Redux actions
 * Automatically sets error state for actions ending with 'Failure'
 */
export const errorMiddleware: Middleware = (store) => (next) => (action) => {
  if (typeof action === "object" && action !== null && "type" in action) {
    const actionType = action.type as string;

    // Handle Failure actions
    if (actionType.endsWith("Failure")) {
      // Hide loader on failure
      store.dispatch(hideLoader());

      // LICENCE_NOT_FOUND from fetchLicenceFailure is handled by DashboardLayout — suppress the modal
      if (
        actionType === "licence/fetchLicenceFailure" &&
        "payload" in action &&
        typeof (action as { payload: unknown }).payload === "object" &&
        (action as { payload: unknown }).payload !== null &&
        ((action as { payload: { code?: string } }).payload).code === "LICENCE_NOT_FOUND"
      ) {
        return next(action);
      }

      // MED-1621: while the PIN screen is shown, calls fail with the lock codes — the lock
      // screen is the answer, not the generic error modal.
      const session = store.getState()?.session;
      if (session?.locked || session?.hasPin === false || session?.forcedLogout) {
        return next(action);
      }

      // Check if the action has an error payload
      if ("payload" in action && action.payload) {
        const payload = action.payload;

        // If payload is a string (error message), create a basic error object
        if (typeof payload === "string") {
          store.dispatch(
            setError({
              code: "UNEXPECTED_ERROR",
              category: "RETRIABLE_UNEXPECTED_ERROR",
              message: payload,
            })
          );
        }
        // If payload has error structure, use it
        else if (
          typeof payload === "object" &&
          "code" in payload &&
          "message" in payload
        ) {
          // Ensure the payload has the required category field
          const errorPayload = payload as {
            code: string;
            message: string;
            category?: ErrorType;
          };
          store.dispatch(
            setError({
              code: errorPayload.code,
              category:
                errorPayload.category || errorType.RETRIABLE_UNEXPECTED_ERROR,
              message: errorPayload.message,
            })
          );
        }
      }
    }
  }

  return next(action);
};
